import { Box, CircularProgress, useTheme } from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'
import React, { forwardRef, useEffect, useRef } from 'react'

export interface VirtualListboxProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactElement[]
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  hasNextPage?: boolean
}

const VirtualListbox = forwardRef<HTMLDivElement, VirtualListboxProps>(
  function Listbox(props, ref) {
    const {
      children,
      isFetchingNextPage,
      fetchNextPage,
      hasNextPage = false,
      ...other
    } = props
    const parentRef = useRef<HTMLUListElement>(null)

    const itemCount = hasNextPage ? children.length + 1 : children.length
    const itemSize = 60

    const rowVirtualizer = useVirtualizer({
      count: itemCount,
      getScrollElement: () => parentRef.current,
      estimateSize: () => itemSize,
      overscan: 5,
    })

    useEffect(() => {
      const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse()

      if (
        lastItem.index >= children.length - 1 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage()
      }
    }, [
      hasNextPage,
      fetchNextPage,
      children.length,
      isFetchingNextPage,
      rowVirtualizer.getVirtualItems(),
    ])

    const theme = useTheme();

    return (
      <Box ref={ref} {...other}>
        <Box
          component="ul"
          ref={parentRef}
          sx={{
            width: '100%',
            height: 'auto',
            maxHeight: 400,
            minHeight: itemSize,
            overflow: 'auto',
            m: 0,
            p: 0,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const isLoaderRow = virtualItem.index > children.length - 1
              const item = children[virtualItem.index]

              if (isLoaderRow) {
                return (
                  <Box
                    key={virtualItem.key}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      p: 2,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )
              }

              // Type assertion for the item props
              const itemProps =
                item.props as React.LiHTMLAttributes<HTMLLIElement> & {
                  onMouseEnter?: (e: React.MouseEvent<HTMLLIElement>) => void
                  onMouseLeave?: (e: React.MouseEvent<HTMLLIElement>) => void
                }

              return (
                <div
                  key={virtualItem.key}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {React.cloneElement(item, {
                    style: {
                      ...itemProps.style,
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      padding: theme.nawy.spacing.xs,
                      margin: 0,
                      boxSizing: 'border-box',
                    } as React.CSSProperties,
                    ...Object.fromEntries(
                      Object.entries(itemProps).filter(
                        ([key]) => !['style'].includes(key),
                      ),
                    ),
                  } as any)}
                </div>
              )
            })}
          </Box>
        </Box>
      </Box>
    )
  },
)

export default VirtualListbox
